/**
 * loaderTokens — which loader belongs where, and how big.
 *
 * Split from the component so the routing rules are testable and so callers
 * pick a *context* ("this is a button") rather than hand-tuning pixels at every
 * one of the ~25 loading sites.
 */

export const LOADER_VARIANTS = ["orbit", "flip", "pulse", "deal"];

export const DEFAULT_VARIANT = "orbit";

/** Base pixel box per size. Everything else scales off this. */
export const LOADER_SIZES = {
  sm: 18, // inline: buttons, list rows
  md: 36, // panels, sections, modals
  lg: 60, // full-page / first paint
};

export const DEFAULT_SIZE = "md";

/**
 * Context → variant. The point is restraint: the everyday spinner is quiet
 * (orbit), and the characterful ones are reserved for moments that are rare
 * enough to earn the attention.
 *
 *   inline      — inside a button or a row. Must not draw the eye.
 *   panel       — a section swapping its contents.
 *   grid        — a wall of cards arriving.
 *   discovery   — the map looking for something.
 *   reveal      — a blocking, branded wait (opening a card).
 */
export const LOADER_CONTEXT = {
  inline: { variant: "orbit", size: "sm" },
  panel: { variant: "orbit", size: "md" },
  grid: { variant: "deal", size: "md" },
  discovery: { variant: "pulse", size: "lg" },
  reveal: { variant: "flip", size: "lg" },
};

/**
 * Resolve a context name to {variant, size}. Unknown contexts fall back to the
 * quiet default rather than throwing — a loading state must never be the thing
 * that breaks a screen.
 */
export const resolveLoaderContext = (context) =>
  LOADER_CONTEXT[context] || { variant: DEFAULT_VARIANT, size: DEFAULT_SIZE };

/** Pixel box for a size name, tolerating a raw number. */
export const loaderPx = (size) => {
  if (typeof size === "number" && size > 0) return size;
  return LOADER_SIZES[size] || LOADER_SIZES[DEFAULT_SIZE];
};

/** Brand ramp, shared with ParticleBurst / celebration surfaces. */
export const LOADER_COLORS = {
  purple: "#8b5cf6",
  purpleDeep: "#6d28d9",
  purpleSoft: "#a78bfa",
  purplePale: "#c4b5fd",
  foil: ["#f472b6", "#7dd3fc", "#fde047"],
};
